import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress
} from '@mui/material';
import { ExpandMore, CheckCircle, Error } from '@mui/icons-material';

/**
 * SMART analysis results dialog
 */
const SmartResultsDialog = ({ 
  open, 
  results, 
  onClose, 
  loading = false, 
  hasPermission = true 
}) => {
  if (!results && !loading) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Typography variant="h5" component="div">
          SMART Analysis
        </Typography>
        {results?.filename && (
          <Typography variant="subtitle2" color="text.secondary">
            {results.filename}
          </Typography>
        )}
      </DialogTitle>
      
      <DialogContent dividers>
        {hasPermission ? (
          loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {/* Summary Statistics */}
              {results?.summary_statistics && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      General Statistics
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">
                          Total Requirements
                        </Typography>
                        <Typography variant="h6">
                          {results.summary_statistics.total_requirements}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">
                          Average Score
                        </Typography>
                        <Typography variant="h6">
                          {results.summary_statistics.average_smart_score?.toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">
                          Maximum Score
                        </Typography>
                        <Typography variant="h6">
                          {results.summary_statistics.max_score?.toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">
                          Minimum Score
                        </Typography>
                        <Typography variant="h6">
                          {results.summary_statistics.min_score?.toFixed(2)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
              {/* Top Requirements */}
              {results?.top_requirements && results.top_requirements.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircle sx={{ mr: 1, color: 'success.main' }} />
                      Top 5 Requirements
                    </Typography>
                    {results.top_requirements.map((req, index) => (
                      <Accordion key={req.id} sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Box display="flex" alignItems="center" width="100%">
                            <Chip 
                              label={`#${index + 1}`} 
                              size="small" 
                              color="success" 
                              sx={{ mr: 2 }} 
                            />
                            <Typography variant="subtitle1" sx={{ flex: 1 }}>
                              {req.notation || req.label_english || req.label}
                            </Typography>
                            <Chip 
                              label={req.smart_score?.toFixed(2)} 
                              color="primary" 
                              size="small" 
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Typography variant="body2">
                            {req.requirement_text_english || req.requirement_text || 'No description'}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </CardContent>
                </Card>
              )}
            </Box>
          )
        ) : (
          <Typography color="error" variant="body1">
            You do not have permission to view these results.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SmartResultsDialog;
